import { getBaseUrl, request } from 'src/helper/api';
import type {
  LumiBrandingType,
  LumiConfigArea,
  YardiMappingRedriveDirection,
  YardiMappingSection,
} from './types';

const ecfBaseUrl = () => `${getBaseUrl()}api/external-connections/`;

/**
 * Retrieves ECF status for the current org: whether ECF is enabled, the list of
 * connections, and whether the LuMi config surface is available. Drives whether
 * the ECF Settings section (and the LuMi Config tab) render at all.
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const getEcfStatus = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${ecfBaseUrl()}status`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/getEcfStatus',
  );

/** Retrieves aggregate operational data for the External Connections overview. */
export const getEcfOverview = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${ecfBaseUrl()}overview`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/getEcfOverview',
  );

/**
 * Retrieves a paginated, filterable page of the event ledger. The dispatched
 * payload becomes the query string (direction/status/event_type/from/to/page/per_page).
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const getEcfEvents = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${ecfBaseUrl()}events`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/getEcfEvents',
  );

/**
 * Retrieves one entity's ECF event lifecycle — the same ledger endpoint narrowed by
 * `work_order_uuid`. It is a SEPARATE thunk from {@link getEcfEvents} purely so the slice can
 * route it to `lifecycle` state instead of the `events` list the ledger page renders.
 *
 * A uuid belonging to another org resolves to an empty page server-side rather than an error.
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const getEcfEventLifecycle = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${ecfBaseUrl()}events`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/getEcfEventLifecycle',
  );

/**
 * Retrieves a single event's detail: redacted payload, signature metadata, and
 * the attempts timeline.
 * @param {string} eventId - The event id.
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const getEcfEventDetails = (eventId: string, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${ecfBaseUrl()}events/${eventId}`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/getEcfEventDetails',
  );

/** Safely requeues one outbound ledger event when the backend marks it eligible. */
export const retryEcfEvent = (eventId: string, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${ecfBaseUrl()}events/${eventId}/retry`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/retryEcfEvent',
  );

/**
 * Retrieves the current LuMi configuration for all five areas. Used to
 * pre-populate the config forms on mount.
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const getEcfLumiConfig = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${ecfBaseUrl()}lumi-config`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/getEcfLumiConfig',
  );

/**
 * Persists a single LuMi config area. The dispatched payload is that area's
 * PUT body. Resolves to `{ status:"ok", area }` or rejects with a 422
 * `{ message, errors }` for inline field errors.
 * @param {LumiConfigArea} area - The config area being saved.
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const updateEcfLumiConfig = (area: LumiConfigArea, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'PUT',
      url: `${ecfBaseUrl()}lumi-config/${area}`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/updateEcfLumiConfig',
  );

/**
 * Uploads a new LuMi logo or favicon. Dispatch a `FormData` whose `image` field is the PNG; it is
 * sent as multipart. Resolves to `{ type, outcome, branding }` where `branding` is the FULL,
 * authoritative logo + favicon state. Rejects with `{ ...body, status }` (422/409/429/503/404).
 * @param {LumiBrandingType} type - `logo` or `favicon`.
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const uploadEcfLumiBranding = (
  type: LumiBrandingType,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${ecfBaseUrl()}lumi-config/branding/${type}`,
      contentType: 'form-data',
      token,
      signal,
    },
    'external-connections/uploadEcfLumiBranding',
  );

/**
 * Clears the current LuMi logo or favicon; the cleared image stays restorable. Dispatch with no
 * payload (a DELETE carries no body). Resolves like {@link uploadEcfLumiBranding}.
 * @param {LumiBrandingType} type - `logo` or `favicon`.
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const clearEcfLumiBranding = (
  type: LumiBrandingType,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'DELETE',
      url: `${ecfBaseUrl()}lumi-config/branding/${type}`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/clearEcfLumiBranding',
  );

/**
 * Restores the previous LuMi logo or favicon. Dispatch `{ hash }` with the slot's `previous.hash`;
 * it is sent as the JSON body, so a concurrent change surfaces as a 409 rather than restoring the
 * wrong image. Resolves like {@link uploadEcfLumiBranding}.
 * @param {LumiBrandingType} type - `logo` or `favicon`.
 * @param {string} [token] - Session access token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const rollbackEcfLumiBranding = (
  type: LumiBrandingType,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${ecfBaseUrl()}lumi-config/branding/${type}/rollback`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/rollbackEcfLumiBranding',
  );

/** Retrieves the complete editable Yardi catalog and connection-scoped mappings. */
export const getEcfYardiMappings = (connectionId: number, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${ecfBaseUrl()}connections/${connectionId}/mappings`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/getEcfYardiMappings',
  );

/** Persists one mapping section with optimistic version and catalog checks. */
export const updateEcfYardiMappings = (
  connectionId: number,
  section: YardiMappingSection,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'PUT',
      url: `${ecfBaseUrl()}connections/${connectionId}/mappings/${section}`,
      contentType: 'json',
      token,
      signal,
    },
    'external-connections/updateEcfYardiMappings',
  );

/** Requeues one connection-scoped Yardi mapping hold after its mapping is corrected. */
export const redriveEcfYardiMappingHold = (
  connectionId: number,
  direction: YardiMappingRedriveDirection,
  heldWorkId: number,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${ecfBaseUrl()}connections/${connectionId}/mappings/redrive/${direction}/${heldWorkId}`,
      contentType: 'json',
      token,
      signal,
    },
    `external-connections/redriveEcfYardiMappingHold/${direction}`,
  );
